"use client";
/**
 * Home-screen challenges widget (Block 5, design 1.6.1). A compact strip
 * surfacing the customer's two most-progressed active challenges with a "See
 * all" link to /challenges.
 *
 * Renders nothing unless the customer is signed in, challenges are enabled,
 * and at least one active challenge exists — so it stays invisible for tenants
 * without gamification and never adds empty chrome to the Home screen.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Card } from "@/components/ui";
import { ProgressBar } from "@/components/loyalty/LoyaltyBits";
import { useChallenges, localizedField } from "@/hooks/useLoyaltyExpansion";
import type { CustomerChallenge } from "@/api/services";
import { useAuth } from "@/store/auth";

function progressOf(c: CustomerChallenge): { value: number; target: number; pct: number } {
  const target = c.target ?? c.criteria?.target ?? 0;
  const value = c.progress ?? 0;
  return { value, target, pct: target > 0 ? value / target : 0 };
}

export function HomeChallengesWidget() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { isLoggedIn } = useAuth();
  const { challengesEnabled, active } = useChallenges();

  // Surface the two challenges closest to completion.
  const top = useMemo(() => {
    return [...active]
      .sort((a, b) => progressOf(b).pct - progressOf(a).pct)
      .slice(0, 2);
  }, [active]);

  if (!isLoggedIn || !challengesEnabled || top.length === 0) return null;

  return (
    <div style={{ padding: "0 16px 4px" }}>
      <Card padding={0} style={{ overflow: "hidden" }}>
        <div
          style={{
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icons.trophy size={16} style={{ color: "var(--primary-700)" }} />
            <span className="display" style={{ fontSize: 13.5 }}>
              {t("v2.challenges.widget_title")}
            </span>
          </div>
          <button
            onClick={() => navigate("/challenges")}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              color: "var(--primary-700)",
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {t("v2.loyalty_hub.see_all")}
            <Icons.chevron size={12} />
          </button>
        </div>

        {top.map((c, i) => {
          const { value, target, pct } = progressOf(c);
          const name = localizedField(c.name, lang) ?? t("v2.challenges.untitled");
          return (
            <button
              key={c.id ?? c.challengeId ?? i}
              onClick={() => navigate("/challenges")}
              style={{
                width: "100%",
                padding: "12px 14px",
                display: "flex",
                gap: 12,
                alignItems: "center",
                background: "transparent",
                border: 0,
                borderTop: i === 0 ? 0 : "1px solid var(--border)",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "start",
              }}
            >
              <div
                style={{
                  display: "grid",
                  placeItems: "center",
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: "var(--primary-50)",
                  color: "var(--primary-700)",
                  flexShrink: 0,
                }}
              >
                <Icons.trophy size={17} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--ink-1)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </span>
                  {target > 0 && (
                    <span className="num" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-2)" }}>
                      {value}/{target}
                    </span>
                  )}
                </div>
                {target > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <ProgressBar value={pct} height={5} />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </Card>
    </div>
  );
}
